{
	"openapi": "3.1.0",
	"info": {
		"description": "The Whop REST API. Please see https://docs.whop.com/developer/api/getting-started for more details.",
		"termsOfService": "https://whop.com/tos-developer-api/",
		"title": "Whop API",
		"version": "1.0.0",
		"x-api-version-date": "2026-09-15"
	},
	"paths": {
		"/files": {
			"get": {
				"description": "Returns the files with the given IDs, newest first — fetch a batch in one request instead of retrieving each file individually. Only files you created are returned; IDs that do not exist, or that another credential created, are omitted. A request for up to 100 IDs answers in a single page by default; a larger batch pages at up to 100 files per response — follow `page_info` with the same `file_ids` to walk the rest.",
				"operationId": "list",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "The files to return, each prefixed `file_`. Repeat the parameter to pass several, up to 250 per request. Batches of up to 100 answer in one page by default; larger batches page at up to 100 per response.",
						"explode": true,
						"in": "query",
						"name": "file_ids",
						"required": true,
						"schema": {
							"items": {
								"example": "file_xxxxxxxxxxxxx",
								"type": "string"
							},
							"type": "array"
						},
						"style": "form"
					},
					{
						"description": "The field to sort by.",
						"in": "query",
						"name": "order",
						"required": false,
						"schema": {
							"enum": ["created_at"],
							"type": "string"
						}
					},
					{
						"description": "The sort direction.",
						"in": "query",
						"name": "direction",
						"required": false,
						"schema": {
							"enum": ["asc", "desc"],
							"type": "string"
						}
					},
					{
						"description": "Number of results to return from the start of the range.",
						"in": "query",
						"name": "first",
						"required": false,
						"schema": {
							"maximum": 100,
							"type": "integer"
						}
					},
					{
						"description": "Return results after this cursor. Use `page_info.end_cursor` from the previous response to fetch the next page.",
						"in": "query",
						"name": "after",
						"required": false,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Number of results to return from the end of the range.",
						"in": "query",
						"name": "last",
						"required": false,
						"schema": {
							"maximum": 100,
							"type": "integer"
						}
					},
					{
						"description": "Return results before this cursor. Use `page_info.start_cursor` from the previous response to fetch the previous page.",
						"in": "query",
						"name": "before",
						"required": false,
						"schema": {
							"type": "string"
						}
					}
				],
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"properties": {
										"data": {
											"items": {
												"$ref": "#/components/schemas/File"
											},
											"type": "array"
										},
										"page_info": {
											"properties": {
												"end_cursor": {
													"type": ["string", "null"]
												},
												"has_next_page": {
													"example": false,
													"type": "boolean"
												},
												"has_previous_page": {
													"example": true,
													"type": "boolean"
												},
												"start_cursor": {
													"example": "WyJjdXJzb3IiLDFd",
													"type": ["string", "null"]
												}
											},
											"required": [
												"end_cursor",
												"start_cursor",
												"has_next_page",
												"has_previous_page"
											],
											"type": "object"
										}
									},
									"required": ["data", "page_info"],
									"type": "object"
								}
							}
						},
						"description": "files listed"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "invalid parameters"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "unauthorized"
					}
				},
				"security": [
					{
						"bearerAuth": []
					}
				],
				"summary": "List Files",
				"tags": ["Files"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "List"
					}
				}
			},
			"post": {
				"description": "Creates a file and returns a presigned destination to upload its bytes to. PUT the bytes to `upload_url` (single-part), or to each of `multipart_upload_urls` and then call Complete File Multipart Upload. Once the bytes land the file becomes `ready`, and its ID can be attached wherever a file is accepted — account legal documents, dispute evidence documents. For a step-by-step walkthrough of single-part and multipart uploads, see the [direct file uploads guide](/developer/guides/direct-file-uploads).",
				"operationId": "create",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"$ref": "#/components/parameters/IdempotencyKey"
					}
				],
				"requestBody": {
					"content": {
						"application/json": {
							"schema": {
								"properties": {
									"byte_size": {
										"description": "The file's size in bytes. Required when `multipart` is `true`. Multipart uploads support at most 10,000 parts of 5MB each (about 50 GB).",
										"example": 15728640,
										"type": "integer"
									},
									"filename": {
										"description": "The name of the file including its extension, e.g. `terms.pdf`.",
										"example": "terms.pdf",
										"type": "string"
									},
									"multipart": {
										"description": "Upload the file in 5MB parts. Required for files larger than 5GB; useful above ~100MB. The file must be larger than 5MB.",
										"example": true,
										"type": "boolean"
									},
									"visibility": {
										"description": "`public` files are served via an unsigned CDN URL — use for assets anyone may see. `private` files are served via a signed, expiring URL — use for sensitive documents. Defaults to `private`.",
										"enum": ["public", "private"],
										"example": "public",
										"type": "string"
									}
								},
								"required": ["filename"],
								"type": "object"
							}
						}
					}
				},
				"responses": {
					"201": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/File"
								}
							}
						},
						"description": "file created with a single-part upload destination"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "bad request"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "unauthorized"
					},
					"409": {
						"$ref": "#/components/responses/Conflict"
					}
				},
				"security": [
					{
						"bearerAuth": []
					}
				],
				"summary": "Create File",
				"tags": ["Files"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "Create"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.files.create({\n    filename: \"terms.pdf\"\n});\n"
					}
				]
			}
		},
		"/files/{id}": {
			"get": {
				"description": "Retrieves a file you uploaded — poll it after uploading the bytes to see `upload_status` become `ready`. Only the creator can retrieve a file this way; a file attached to another resource is read through that resource.",
				"operationId": "get",
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/File"
								}
							}
						},
						"description": "file retrieved"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "unauthorized"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "not found"
					}
				},
				"security": [
					{
						"bearerAuth": []
					}
				],
				"summary": "Retrieve File",
				"tags": ["Files"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "Retrieve"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.files.retrieve({\n    id: \"id\"\n});\n"
					}
				],
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "The unique identifier of the file, prefixed `file_`.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					}
				]
			}
		},
		"/files/{id}/complete": {
			"post": {
				"description": "Assembles the parts of a multipart upload after every part has been PUT to its presigned URL. Pass the `multipart_upload_id` from Create File and each part's `ETag` response header. For a step-by-step walkthrough of multipart uploads, see the [direct file uploads guide](/developer/guides/direct-file-uploads).",
				"operationId": "complete",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "The unique identifier of the file, prefixed `file_`.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					},
					{
						"$ref": "#/components/parameters/IdempotencyKey"
					}
				],
				"requestBody": {
					"content": {
						"application/json": {
							"schema": {
								"properties": {
									"multipart_parts": {
										"description": "Every uploaded part, in order.",
										"items": {
											"properties": {
												"etag": {
													"description": "The `ETag` response header from the part's upload.",
													"example": "etag-1",
													"type": "string"
												},
												"part_number": {
													"description": "The 1-based index of the part.",
													"example": 1,
													"type": "integer"
												}
											},
											"required": ["part_number", "etag"],
											"type": "object"
										},
										"type": "array"
									},
									"multipart_upload_id": {
										"description": "The ID of the multipart upload, returned by Create File.",
										"example": "upload-id",
										"type": "string"
									}
								},
								"required": ["multipart_upload_id", "multipart_parts"],
								"type": "object"
							}
						}
					}
				},
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/File"
								}
							}
						},
						"description": "multipart upload completed"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "bad request"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "unauthorized"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "not found"
					},
					"409": {
						"$ref": "#/components/responses/Conflict"
					}
				},
				"security": [
					{
						"bearerAuth": []
					}
				],
				"summary": "Complete File Multipart Upload",
				"tags": ["Files"],
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.files.complete({\n    id: \"id\",\n    multipart_parts: [{\n            etag: \"etag-1\",\n            part_number: 1\n        }],\n    multipart_upload_id: \"upload-id\"\n});\n"
					}
				]
			}
		}
	},
	"components": {
		"schemas": {
			"File": {
				"properties": {
					"content_type": {
						"description": "The file's MIME type, e.g. `application/pdf`.",
						"example": "application/pdf",
						"type": ["string", "null"]
					},
					"created_at": {
						"description": "When the file was created, as an ISO 8601 timestamp.",
						"example": "2026-01-01T12:00:00.000Z",
						"type": "string"
					},
					"filename": {
						"description": "The original filename, including its extension.",
						"example": "evidence.pdf",
						"type": ["string", "null"]
					},
					"id": {
						"description": "The file's ID, prefixed `file_`.",
						"example": "file_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"multipart_chunk_size": {
						"description": "The byte size each part (except the last) must be. Present only on create, and only for multipart uploads.",
						"example": 5242880,
						"type": ["integer", "null"]
					},
					"multipart_upload_id": {
						"description": "The ID of the multipart upload, passed back to `complete`. Present only on create, and only for multipart uploads.",
						"example": "upload-id",
						"type": ["string", "null"]
					},
					"multipart_upload_urls": {
						"items": {
							"$ref": "#/components/schemas/FileMultipartUrl",
							"description": "The presigned URL for each part. Present only on create, and only for multipart uploads."
						},
						"type": ["array", "null"]
					},
					"object": {
						"description": "The type of this object, always `file`.",
						"example": "file",
						"type": "string"
					},
					"size": {
						"description": "The file size in bytes. `null` until the upload has finished.",
						"example": 9670,
						"type": ["integer", "null"]
					},
					"upload_headers": {
						"description": "Headers to send with the upload PUT. Present only on create.",
						"example": {},
						"type": "object"
					},
					"upload_status": {
						"description": "Where the file is in its upload lifecycle.",
						"enum": ["pending", "processing", "ready", "failed"],
						"example": "pending",
						"type": "string"
					},
					"upload_url": {
						"description": "Presigned URL to PUT the file's bytes to. Present only on create, and only for single-part uploads.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx/application.pdf",
						"type": ["string", "null"]
					},
					"url": {
						"description": "A URL to download the file: a permanent CDN URL for public files, a signed expiring URL for private ones. `null` until the upload has finished.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/audio/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
						"type": ["string", "null"]
					},
					"visibility": {
						"description": "`public` files are served via an unsigned CDN URL; `private` files via a signed, expiring URL.",
						"enum": ["public", "private"],
						"example": "private",
						"type": "string"
					}
				},
				"required": [
					"object",
					"id",
					"filename",
					"content_type",
					"size",
					"url",
					"upload_status",
					"visibility",
					"created_at"
				],
				"type": "object"
			},
			"FileMultipartUrl": {
				"properties": {
					"part_number": {
						"description": "The 1-based index of this part within the multipart upload.",
						"example": 1,
						"type": "integer"
					},
					"url": {
						"description": "The presigned URL to PUT this part's bytes to.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx/application.pdf",
						"type": "string"
					}
				},
				"required": ["part_number", "url"],
				"type": "object"
			},
			"V1ErrorResponse": {
				"properties": {
					"error": {
						"properties": {
							"code": {
								"description": "Machine-readable reason for this specific refusal, such as `bank_warning_not_acknowledged`. Only present when the error carries one.",
								"type": "string"
							},
							"message": {
								"description": "Human-readable error message.",
								"example": "account_id is required",
								"type": "string"
							},
							"type": {
								"description": "Machine-readable error code.",
								"example": "bad_request",
								"type": "string"
							}
						},
						"required": ["type", "message"],
						"type": "object"
					}
				},
				"required": ["error"],
				"type": "object"
			}
		},
		"parameters": {
			"ApiVersionDate": {
				"description": "Pins the request to a dated API version.",
				"in": "header",
				"name": "Api-Version-Date",
				"required": false,
				"schema": {
					"example": "2026-09-15",
					"type": "string"
				}
			},
			"IdempotencyKey": {
				"description": "A unique key that makes this request safe to retry. See [Idempotent requests](https://docs.whop.com/developer/api/idempotency).",
				"in": "header",
				"name": "Idempotency-Key",
				"required": false,
				"schema": {
					"example": "d9105228-4a08-46b1-8b91-42fed586d383",
					"maxLength": 255,
					"type": "string"
				}
			}
		},
		"responses": {
			"InvalidParameters": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Invalid Parameters"
			},
			"Unauthorized": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Unauthorized"
			},
			"Conflict": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Conflict"
			},
			"NotFound": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Resource not found"
			}
		}
	}
}
